import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ConfigService } from '@app/core/config/config.service';
import { GlobalLoaderService } from '@app/utils/services/global-loader.service';

/**
 * Patrones de URL de endpoints relacionados a uploads de archivos.
 * Estos NO deben disparar el modal global — cada slot tiene su propio indicador.
 * Se filtra por sub-ruta concreta para no excluir otros endpoints de
 * `/documents/` (p.ej. `download-all` en back-office sí muestra el modal).
 */
const UPLOAD_ENDPOINT_PATTERNS = [
  /\/documents\/upload(\b|\/|\?|$)/,
  /\/documents\/upload-url(\b|\/|\?|$)/,
  /\/documents\/presigned-url(\b|\/|\?|$)/,
  /\/documents\/confirm(\b|\/|\?|$)/,
];

/**
 * Muestra el modal de carga global durante las peticiones al API que generan
 * transiciones de pantalla. Combinado con el listener de router events en
 * AppComponent, mantiene el overlay visible desde que el usuario dispara una
 * acción hasta que la siguiente pantalla ya está cargada.
 *
 * Se excluyen:
 * - Peticiones a S3 (PUT de archivos — tienen su propio indicador per-slot)
 * - Endpoints de upload del API (presigned-url/upload/confirm)
 * - Cualquier URL que no pertenezca al API del backend (assets, i18n, etc.)
 */
export const globalLoaderInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url) || isUploadRequest(req.url)) {
    return next(req);
  }

  const loader = inject(GlobalLoaderService);
  loader.show();
  return next(req).pipe(finalize(() => loader.hide()));
};

function isApiRequest(url: string): boolean {
  // Excluir S3 (uploads usan sus propios spinners per-slot)
  if (isS3Request(url)) return false;

  // Solo mostrar loader para peticiones que van al backend configurado
  const configService = inject(ConfigService);
  const apiBase = configService.baseUrl;
  if (!apiBase) return false;
  return url.startsWith(apiBase);
}

function isUploadRequest(url: string): boolean {
  return UPLOAD_ENDPOINT_PATTERNS.some(pattern => pattern.test(url));
}

function isS3Request(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return (
      host.includes('s3.amazonaws.com') ||
      host.includes('.s3.') ||
      host.startsWith('s3.')
    );
  } catch {
    return false;
  }
}
